import React from 'react';
import { useQuery } from '@apollo/client';
import { useParams, useNavigate } from 'react-router-dom';
import { GET_PASSAGE_PLAN } from '../graphql/queries';
import LoadingSpinner from './LoadingSpinner';
import ErrorMessage from './ErrorMessage';
import './PassagePlanDetail.css';

const PassagePlanDetail = () => {
  const { passagePlanId } = useParams();
  const navigate = useNavigate();
  const { loading, error, data } = useQuery(GET_PASSAGE_PLAN, {
    variables: { id: passagePlanId }
  });

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message="航行情報の取得に失敗しました" />;
  if (!data?.getPassagePlan) return <ErrorMessage message="航行情報が見つかりません" />;

  const passagePlan = data.getPassagePlan;
  const ship = passagePlan.ship;

  const formatDate = (dateString) => {
    if (!dateString) return '未定';
    return new Date(dateString).toLocaleString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getDirectionText = (direction) => {
    if (!direction) return '不明';
    switch (direction) {
      case 'north':
        return '北航船';
      case 'south':
        return '南航船';
      default:
        return '不明';
    }
  };

  const getDestinationLabel = (direction) => {
    if (!direction) return '入港/出港';
    switch (direction) {
      case 'north':
        return '入港';
      case 'south':
        return '出港';
      default:
        return '入港/出港';
    }
  };

  const getShipTypeIcon = (kind) => {
    const defaultIcon = '/static/default-ship-icon.png';

    // アイコンURLが提供されている場合はそれを使用
    if (kind?.iconUrl) {
      return (
        <img
          src={kind.iconUrl}
          alt={kind.name || '船種'}
          className="ship-type-icon"
          onError={(e) => {
            // 画像の読み込みに失敗した場合はデフォルト画像を表示
            e.target.src = defaultIcon;
          }}
        />
      );
    }

    // iconUrlがない場合はデフォルト画像を表示
    return (
      <img
        src={defaultIcon}
        alt={kind?.name || '船種'}
        className="ship-type-icon"
      />
    );
  };

  const getStatusInfo = (status) => {
    switch (status) {
      case 'fresh':
        return { text: '新規', color: '#4CAF50', icon: '✨' };
      case 'alive':
        return { text: '有効', color: '#2196F3', icon: '✅' };
      case 'updated':
        return { text: '変更あり', color: '#FF9800', icon: '🔄' };
      case 'ongoing':
        return { text: '進行中', color: '#9C27B0', icon: '🚀' };
      case 'expired':
        return { text: '期限切れ', color: '#F44336', icon: '⏰' };
      case 'deleted':
        return { text: '削除済み', color: '#757575', icon: '🗑️' };
      case 'ambiguous':
        return { text: '不明確', color: '#FF5722', icon: '❓' };
      case 'unknown':
      default:
        return { text: '不明', color: '#9E9E9E', icon: '❓' };
    }
  };

  const statusInfo = getStatusInfo(passagePlan.status);

  const handleShipClick = () => {
    navigate(`/ship/${ship.id}`);
  };

  return (
    <div className="passage-plan-detail">
      <div className="passage-plan-detail-header">
        <button
          className="back-button"
          onClick={() => navigate(-1)}
        >
          ← 戻る
        </button>
        <h1>航行情報詳細</h1>
      </div>

      <div className="passage-plan-detail-content">
        <div className="passage-plan-info-card">
          <div className="passage-plan-header">
            <h2>航行情報</h2>
            <span
              className="status-badge"
              style={{ backgroundColor: statusInfo.color }}
            >
              {statusInfo.icon} {statusInfo.text}
            </span>
          </div>

          <div className="passage-plan-details">
            <div className="detail-row">
              <span className="detail-label">{getDestinationLabel(passagePlan.direction)}:</span>
              <span className="detail-value">{passagePlan.destination?.name || '不明'}</span>
            </div>

            <div className="detail-row">
              <span className="detail-label">方向:</span>
              <span className="detail-value">{getDirectionText(passagePlan.direction)}</span>
            </div>

            <div className="detail-row">
              <span className="detail-label">入航予定:</span>
              <span className="detail-value">{formatDate(passagePlan.enterAt)}</span>
            </div>
          </div>

          <div className="passage-flags">
            {passagePlan.viaWaypoint && (
              <span className="flag via-waypoint">中ノ瀬</span>
            )}
            {passagePlan.withPilot && (
              <span className="flag with-pilot">水先人</span>
            )}
          </div>
        </div>

        <div className="ship-info-card">
          <div className="ship-info-header">
            <h2>船舶情報</h2>
            <button
              className="view-ship-button"
              onClick={handleShipClick}
            >
              船舶詳細を見る →
            </button>
          </div>

          <div className="ship-info-content">
            <div className="ship-basic-info">
              <div className="ship-icon">
                {getShipTypeIcon(ship.kind)}
              </div>
              <div className="ship-info">
                <h3 className="ship-name">{ship.name || '船名不明'}</h3>
                <p className="ship-type">{ship.kind?.name || '船種不明'}</p>
              </div>
              <div className="ship-badges">
                {ship.huge && <span className="huge-flag">巨大船</span>}
              </div>
            </div>

            <div className="ship-details">
              <div className="detail-row">
                <span className="detail-label">旗国:</span>
                <span className="detail-value flag-nation">
                  {ship.flagNation?.imageUrl && (
                    <img
                      src={ship.flagNation.imageUrl}
                      alt={ship.flagNation.name || '旗国'}
                      className="flag-image"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  )}
                  <span className="flag-text">
                    {ship.flagNation?.name || '不明'}
                    {ship.flagNation?.abbreviation && ` (${ship.flagNation.abbreviation})`}
                  </span>
                </span>
              </div>

              {ship.length && (
                <div className="detail-row">
                  <span className="detail-label">長さ:</span>
                  <span className="detail-value">{ship.length}m</span>
                </div>
              )}

              {ship.tonnage && (
                <div className="detail-row">
                  <span className="detail-label">トン数:</span>
                  <span className="detail-value">{ship.tonnage.toLocaleString()}t</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PassagePlanDetail;
